<script setup lang="ts">
import IconFindSm from "~/assets/icons/find/find-sm.svg?component"
import { PropertyId } from "~/classes/entity"

const { state: instantState, onSearch, onInput, autocompleteResults, keyboardSelectedFile, clearKeyboardSelection, closeInstantMenu, addSearchFacet } = useInstantMenu()
const { navigateToFile } = useNavigation()
const { navIgnoreMainViewGestures } = useGlobals()
const input = useTemplateRef("query")
const filled = computed(() => instantState.value.query.trim().length > 0)
const overlay = useTemplateRef("overlay")

const removeFacet = (index: number) => {
  instantState.value.facets.splice(index, 1)
}

const removeSort = (index: number) => {
  instantState.value.sorts.splice(index, 1)
}

const onTextInteraction = () => {
  // clear the keyboard navigation as we inputted text
  clearKeyboardSelection()
}

const onClickInteraction = () => {
  clearKeyboardSelection()
}

const navigateAndClose = (file: StaticFile) => {
  navigateToFile(file)
  closeInstantMenu()
}

function handleClickOutside(event: MouseEvent) {
  if (!event.target)
    return
  if (event.target !== overlay.value)
    return

  closeInstantMenu()
}

function addBlankFileInFacet() {
  addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_PATH, facetType: { fileIn: "" } })
}

function addBlankThemeFacet() {
  addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_EMBEDDINGS, facetType: { nearDocLikeText: "" } })
}

function addBlankTagFacet() {
  addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_TAGS, facetType: { hasTag: "" } })
}

watch(() => instantState.value.query, () => onInput())

onMounted(() => setTimeout(() => input.value?.element?.focus(), 100))
</script>

<template>
  <dropdown-menu class="nav-instant-menu">
    <menu-section class="instant-query">
      <div v-if="instantState.loading" class="spinner">
        <interface-spinner class="spinner-inner" />
      </div>
      <icon-find-sm v-else class="icon" />
      <interface-content-editable
        ref="query"
        v-model="instantState.query"
        tag="div"
        wrap
        class="query"
        :class="{ filled }"
        contenteditable="plaintext-only"
        @update:model-value="onTextInteraction()"
      />
    </menu-section>
    <menu-separator />
    <menu-section>
      <div class="facets">
        <template v-for="facet, idx in instantState.facets" :key="idx">
          <nav-instant-facet-color-picker
            v-if="'nearColor' in instantState.facets[idx].facetType"
            v-model:near-color="instantState.facets[idx].facetType.nearColor"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-file-picker
            v-else-if="'nearFile' in instantState.facets[idx].facetType"
            v-model:near-file="instantState.facets[idx].facetType.nearFile"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-theme-picker
            v-else-if="'nearDocLikeText' in instantState.facets[idx].facetType"
            v-model:query="instantState.facets[idx].facetType.nearDocLikeText"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-theme-picker
            v-else-if="'nearImageText' in instantState.facets[idx].facetType"
            v-model:query="instantState.facets[idx].facetType.nearImageText"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-theme-picker
            v-else-if="'nearVideoText' in instantState.facets[idx].facetType"
            v-model:query="instantState.facets[idx].facetType.nearVideoText"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-folder-picker
            v-else-if="'fileIn' in instantState.facets[idx].facetType"
            v-model:in="instantState.facets[idx].facetType.fileIn"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
            @replace="(facet: SearchFacet) => instantState.facets[idx] = facet"
          />
          <nav-instant-facet-folder-picker
            v-else-if="'fileOnlyIn' in instantState.facets[idx].facetType"
            v-model:in="instantState.facets[idx].facetType.fileOnlyIn"
            only-in
            @replace="(facet: SearchFacet) => instantState.facets[idx] = facet"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
          />
          <nav-instant-facet-tag-picker
            v-else-if="'hasTag' in instantState.facets[idx].facetType"
            v-model:tag="instantState.facets[idx].facetType.hasTag"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
            @replace="(facet: SearchFacet) => instantState.facets[idx] = facet"
          />
          <nav-instant-facet-filter
            v-else
            v-model:facet="instantState.facets[idx]"
            @click="onClickInteraction()"
            @remove="removeFacet(idx)"
          />
        </template>
        <template v-for="{ propertyId }, idx in instantState.sorts" :key="idx">
          <nav-instant-sort-item
            v-model:descending="instantState.sorts[idx].descending"
            :property-id
            @remove="removeSort(idx)"
          />
        </template>
        <nav-instant-shortcut label="Locations" @click="addBlankFileInFacet()" />
        <nav-instant-dropdown-similarity />
        <nav-instant-shortcut label="Theme" @click="addBlankThemeFacet()" />
        <nav-instant-shortcut label="Tags" @click="addBlankTagFacet()" />
        <nav-instant-dropdown-sort />
        <nav-instant-dropdown-filter />
        <!-- <nav-instant-shortcut label="People" shortcut="@" /> -->
        <!-- <nav-instant-shortcut label="Properties" shortcut="+" /> -->
      </div>
    </menu-section>
    <menu-separator />
    <menu-section>
      <menu-header label="Actions" />
      <nav-instant-item type="ask-ai" />
    </menu-section>
    <menu-separator
      v-if="autocompleteResults.length > 0"
    />
    <menu-section v-if="autocompleteResults.length > 0">
      <menu-header label="Suggested Results" />
      <file-detail
        v-for="file in autocompleteResults"
        :key="file.id"
        :file
        :hovered="keyboardSelectedFile?.id === file.id"
        @click="navigateAndClose(file)"
      />
    </menu-section>
    <menu-separator />
    <menu-section v-if="true">
      <div class="submit-actions">
        <div class="left" />
        <div class="right">
          <div class="nav-search-button" @click="onSearch()">
            <div>
              Search
            </div>
            <menu-shortcut>↵</menu-shortcut>
          </div>
        </div>
      </div>
    </menu-section>
    <!-- TODO: file browsing mode? -->
    <menu-section v-else>
      <div class="submit-actions">
        <div class="left">
          <div>Add to search</div>
          <menu-shortcut>Tab</menu-shortcut>
        </div>
        <div class="right">
          <div>
            Navigate to
          </div>
          <menu-shortcut>↵</menu-shortcut>
        </div>
      </div>
    </menu-section>
  </dropdown-menu>
  <div
    ref="overlay"
    class="overlay"
    :class="[navIgnoreMainViewGestures]"
    @mousedown="e => handleClickOutside(e)"
  />
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.overlay
  @apply fixed top-0 left-0 right-0 bottom-0 pointer-events-auto

.nav-instant-menu
  @apply w-120 flex-grow-1

.instant-query
  @apply flex flex-row
  .icon
    @apply text-[#{$global-stroke-icon-secondary}] flex-none h-5 w-5
  .spinner
    @apply text-[#{$global-stroke-icon-secondary}] flex-none h-5 w-5
    @apply flex items-center justify-center
    .spinner-inner
      @apply w-4 h-4

  .query
    @apply outline-none bg-transparent
    @apply w-full h-full flex-grow-1 -mt-0.5
    @apply t-instant-query caret-$global-blue-primary

.facets
  @apply flex flex-wrap gap-[#{$menu-gap-items}] column-gap-[#{$menu-gap-items}]
.submit-actions
  @apply flex justify-between w-full t-menu-sm text-[#{$menu-t-header}]
  .left, .right
    @apply flex items-center gap-1.5

.nav-search-button
  @apply inline-flex items-center gap-1.5 p-1 -m-1 rounded-[#{$menu-br-item}] cursor-pointer
  &:hover
    @apply bg-[#{$menu-bg-item-hover}]
</style>
